import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress, TextField, MenuItem, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { bucketTotalsOf, BUCKET_OF, READY_GREEN, ASSUM_ORANGE, BLOCKED_RED, REVIEW_PURPLE } from "../methodologyUtils";
import PackagingTracePanel from "./PackagingTracePanel";

// Packaging as a first-class "Por entidad" view. The backend has no groupBy=packaging (nor a
// packaging trace endpoint), so we PIVOT the groupBy=article response client-side: every gap there
// is tagged with entityKind='packaging' + entityId + entityName, regrouped by packaging entity.
// Each row opens the packaging ficha (Pendientes | Trazabilidad = dónde se usa) — no inline expand.

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 14,
    cursor: "pointer",
    "&:hover": { background: theme.palette.action.hover },
  },
  name: { fontWeight: 700, color: "#4f46e5" },
  grow: { flex: "1 1 auto", minWidth: 0 },
  colDep: { width: 150, textAlign: "right", fontSize: 12, color: theme.palette.text.secondary },
  headRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
}));

// Urgency order to keep the most actionable status when a field repeats across articles.
const RANK = { faltan: 0, revisar: 1, asumibles: 2, asumidos: 3, aportados: 4 };
const rankOf = (status) => RANK[BUCKET_OF[status] || "faltan"] ?? 0;

const Buckets = ({ statusCounts }) => {
  const b = bucketTotalsOf(statusCounts);
  return (
    <span style={{ display: "flex", gap: 10, fontSize: 12, fontWeight: 600 }}>
      {b.faltan > 0 && <span style={{ color: BLOCKED_RED }}>{b.faltan} falta</span>}
      {b.revisar > 0 && <span style={{ color: REVIEW_PURPLE }}>{b.revisar} revisar</span>}
      {b.asumidos + b.asumibles > 0 && <span style={{ color: ASSUM_ORANGE }}>{b.asumidos + b.asumibles} asumido</span>}
      {b.aportados > 0 && <span style={{ color: READY_GREEN }}>{b.aportados} ok</span>}
    </span>
  );
};

const PackagingGapList = ({ api, projectId, contracted, onChanged, reloadKey = 0 }) => {
  const classes = useStyles();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("faltan");
  const [open, setOpen] = useState(null); // { entityId, entityName, gaps, articles }

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const r = await api("GET", `/projects/${projectId}/gaps?groupBy=article`);
    setLoading(false);
    const articleGroups = r.ok ? r.data.groups ?? [] : [];

    // Pivot packaging gaps by packaging entity, deduping by field (keep most urgent, merge gapIds
    // across articles so resolving the field clears every underlying gap).
    const byEntity = new Map();
    articleGroups.forEach((art) =>
      (art.gaps || []).forEach((g) => {
        if (g.entityKind !== "packaging") return;
        const pid = g.entityId || g.entityName;
        if (!pid) return;
        let e = byEntity.get(pid);
        if (!e) {
          e = { entityId: g.entityId ?? pid, entityName: g.entityName ?? null, fields: new Map(), articles: new Map() };
          byEntity.set(pid, e);
        }
        if (g.articleId) e.articles.set(g.articleId, g.articleName || g.articleId.slice(0, 8));
        const ids = g.gapIds && g.gapIds.length ? g.gapIds : [g.id];
        const prev = e.fields.get(g.fieldDefinitionId);
        if (!prev) {
          e.fields.set(g.fieldDefinitionId, { ...g, gapIds: [...ids] });
        } else {
          const mergedIds = [...new Set([...prev.gapIds, ...ids])];
          if (rankOf(g.status) < rankOf(prev.status)) e.fields.set(g.fieldDefinitionId, { ...g, gapIds: mergedIds });
          else prev.gapIds = mergedIds;
        }
      })
    );

    const built = [...byEntity.values()].map((e) => {
      const gaps = [...e.fields.values()];
      const statusCounts = {};
      gaps.forEach((x) => (statusCounts[x.status] = (statusCounts[x.status] ?? 0) + 1));
      return {
        entityId: e.entityId,
        entityName: e.entityName,
        gaps,
        statusCounts,
        articles: [...e.articles.entries()].map(([id, name]) => ({ id, name })),
        dependentArticles: e.articles.size,
      };
    });
    setGroups(built);
  }, [api, projectId, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingOf = (sc) => {
    const b = bucketTotalsOf(sc);
    return b.faltan + b.revisar;
  };
  const sortVal = (g) => (sortBy === "faltan" ? pendingOf(g.statusCounts) : g.dependentArticles ?? 0);
  const displayGroups = groups
    .filter((g) => !search.trim() || (g.entityName || "").toLowerCase().includes(search.trim().toLowerCase()))
    .slice()
    .sort((a, b) => sortVal(b) - sortVal(a));

  return (
    <div>
      <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12 }}>
        <TextField
          size="small"
          placeholder="Buscar packaging…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField select size="small" label="Ordenar por" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ minWidth: 220 }}>
          <MenuItem value="faltan">Más gaps por resolver</MenuItem>
          <MenuItem value="dependentArticles">Más artículos afectados</MenuItem>
        </TextField>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>{displayGroups.length} packaging</span>
      </Box>

      {loading ? (
        <Box textAlign="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : !displayGroups.length ? (
        <Box py={3} textAlign="center" style={{ color: "#94a3b8" }}>
          {search ? `Sin packaging que coincida con "${search}"` : "Sin packaging con gaps"}
        </Box>
      ) : (
        <>
          <div className={classes.headRow}>
            <span className={classes.grow}>packaging</span>
            <span style={{ width: 120 }}>gaps</span>
            <span className={classes.colDep}>🔓 al completar</span>
          </div>
          {displayGroups.map((g) => (
            <div key={g.entityId} className={classes.row} onClick={() => setOpen(g)}>
              <span className={classes.grow}>
                <span className={classes.name}>{g.entityName || g.entityId.slice(0, 8)}</span>
              </span>
              <span style={{ width: 120 }}>
                <Buckets statusCounts={g.statusCounts} />
              </span>
              <span className={classes.colDep} title="Artículos que usan este packaging">
                🔓 {g.dependentArticles} art
              </span>
            </div>
          ))}
        </>
      )}

      {open && (
        <PackagingTracePanel
          api={api}
          packagingId={open.entityId}
          packagingName={open.entityName}
          gaps={open.gaps}
          articles={open.articles}
          contracted={contracted}
          onClose={() => setOpen(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

export default PackagingGapList;
